'use client';

import { Button } from '@/components/ui/button';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { useNotifications } from '@/hooks/useNotifications';
import { Bell, BellOff, Settings2 } from 'lucide-react';
import { useState } from 'react';

interface NotificationCenterProps {
  onTokenReceived?: (token: string) => void;
}

export default function NotificationCenter({ onTokenReceived }: NotificationCenterProps) {
  const [isOpen, setIsOpen] = useState(false);
  const { permission, token, tokenId, isLoading, requestPermission, removePermission, forceSaveToken } =
    useNotifications();

  const handleActivateNotifications = async () => {
    const receivedToken = await requestPermission();
    if (receivedToken && onTokenReceived) {
      onTokenReceived(receivedToken);
    }
    setIsOpen(false);
  };

  const getStatusColor = () => {
    switch (permission) {
      case 'granted':
        return 'text-emerald-600';
      case 'denied':
        return 'text-red-600';
      default:
        return 'text-gray-600';
    }
  };

  const getStatusText = () => {
    switch (permission) {
      case 'granted':
        return 'Notificações ativas';
      case 'denied':
        return 'Notificações bloqueadas';
      default:
        return 'Notificações desativadas';
    }
  };

  return (
    <Popover open={isOpen} onOpenChange={setIsOpen}>
      <PopoverTrigger asChild>
        <Button variant='ghost' className='relative h-8 w-8 p-0'>
          {permission === 'granted' ? (
            <Bell className='h-4 w-4 text-emerald-600' />
          ) : (
            <BellOff className='h-4 w-4 text-gray-600' />
          )}
          {permission === 'granted' && <div className='absolute -right-1 -top-1 h-2 w-2 rounded-full bg-emerald-500' />}
        </Button>
      </PopoverTrigger>
      <PopoverContent className='w-80 p-4' align='end'>
        <div className='space-y-4'>
          <div className='flex items-center gap-3'>
            {permission === 'granted' ? (
              <Bell className='h-5 w-5 text-emerald-600' />
            ) : permission === 'denied' ? (
              <BellOff className='h-5 w-5 text-red-600' />
            ) : (
              <Bell className='h-5 w-5 text-gray-600' />
            )}
            <div className='flex-1'>
              <h3 className='font-medium'>Centro de Notificações</h3>
              <p className={`text-sm ${getStatusColor()}`}>{getStatusText()}</p>
            </div>
          </div>

          {permission === 'default' && (
            <div className='space-y-3'>
              <p className='text-sm text-gray-600'>
                Ative as notificações para receber atualizações importantes em tempo real.
              </p>
              <Button
                onClick={handleActivateNotifications}
                disabled={isLoading}
                variant='outline-primary'
                className='w-full'
                isLoading={isLoading}>
                <Bell className='mr-2 h-4 w-4' />
                {isLoading ? 'Ativando...' : 'Ativar Notificações'}
              </Button>
            </div>
          )}

          {permission === 'granted' && (
            <div className='space-y-3'>
              <p className='text-sm text-emerald-700'>✓ Você receberá notificações push neste dispositivo.</p>

              {process.env.NODE_ENV !== 'production' && token && (
                <div className='rounded-lg bg-gray-50 p-3'>
                  <p className='mb-1 text-xs font-medium text-gray-700'>Token FCM:</p>
                  <code className='block break-all text-xs text-gray-600'>{token.substring(0, 50)}...</code>
                  <button
                    onClick={() => {
                      navigator.clipboard.writeText(token);
                    }}
                    className='mt-1 text-xs text-blue-600 hover:text-blue-800'>
                    Copiar token completo
                  </button>
                </div>
              )}

              {tokenId && <p className='text-xs text-gray-600'>ID no backend: {tokenId}</p>}

              {token && !tokenId && (
                <Button
                  onClick={async () => {
                    await forceSaveToken();
                  }}
                  disabled={isLoading}
                  variant='outline'
                  className='w-full border-blue-200 text-blue-600 hover:bg-blue-50'
                  isLoading={isLoading}>
                  Salvar Token no Backend
                </Button>
              )}

              <Button
                onClick={async () => {
                  await removePermission();
                  setIsOpen(false);
                }}
                disabled={isLoading}
                variant='outline'
                className='w-full border-red-200 text-red-600 hover:bg-red-50'
                isLoading={isLoading}>
                Desativar Notificações
              </Button>
            </div>
          )}

          {permission === 'denied' && (
            <div className='space-y-3'>
              <p className='text-sm text-red-700'>As notificações foram bloqueadas pelo navegador.</p>
              <div className='flex items-center gap-2 rounded-lg bg-red-50 p-3'>
                <Settings2 className='h-4 w-4 text-red-600' />
                <p className='text-xs text-red-600'>Ative nas configurações do navegador para receber notificações.</p>
              </div>
            </div>
          )}
        </div>
      </PopoverContent>
    </Popover>
  );
}
